import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import IntersectionObserver from '@researchgate/react-intersection-observer';

import { Text, Grid, Box } from '@simplywallst/ui-core';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';

import { useNormalisedCompanySnowflakeScore } from '@/hooks/useNormalisedCompanySnowflakeScore';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import { TableRowCell } from '../../Table';
import { ValuationMetricTooltip, GrowthMetricTooltip } from '../../Tooltips';

import type { WatchlistItem } from '@/hooks/useWatchlist';
import type { Period } from '@/utilities/timeSeriesPeriodMap';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import Tooltip from '@/components/Tooltip';
import { DeprecatedNarrativeMiniPriceChart } from '@/components/MiniPriceChart';
import { DeferRender } from '@/components/MiniPriceChart';

import {
  formatterCurrency,
  formatterPercentage,
  formatterFactor,
} from '@/utilities/format';

import { useCompanyPeriodReturn } from '../../../useCompanyPeriodReturn';

import { TableRowWrapper, TooltipLabel } from './styled';

import { MoreOptionsProps } from '../../MoreOptions/MoreOptions';

import type { Holding } from '@/hooks/portfolio/usePortfolio';

type Props = {
  index: number;
  listItem: WatchlistItem | Holding;
  onItemSelect: (id: string | null) => void;
  period: Period;
  moreOptions?: React.ReactElement<MoreOptionsProps>;
  primaryNarrative?: React.ReactNode;
};

export const RowExpanded = ({
  index,
  listItem,
  onItemSelect,
  period,
  moreOptions,
  primaryNarrative,
}: Props) => {
  const { id: itemId, company } = listItem;
  const {
    tickerSymbol,
    id: companyId,
    name: companyName,
    canonicalURL,
  } = company;

  const companyCurrencyISO =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  const {
    revenueGrowthAnnual: salesGrowth = null,
    netIncomeGrowthAnnual: earningsGrowth = null,
  } = company.analysisFuture || {};

  const { analystCount } = company.analysisMisc;

  const { return: companyReturn } = useCompanyPeriodReturn(company, period);
  const narrativeFairValue = listItem.company.myValuation?.fairValue ?? null;

  const [isSticky, setIsSticky] = useState(false);

  const {
    preferredRelativeMultiple = null,
    pe = null,
    pb = null,
    priceToSales = null,
    lastSharePrice = null,
    npvPerShare = null,
    priceTarget = null,
    return7d = null,
  } = company.analysisValue || {};

  const valuation = useMemo(() => {
    if (preferredRelativeMultiple === 'pe') return pe;
    if (preferredRelativeMultiple === 'pb') return pb;
    if (preferredRelativeMultiple === 'ps') return priceToSales;
    return null;
  }, [preferredRelativeMultiple, pe, pb, priceToSales]);

  const { Track, trackEvent } = useTracking({
    canonicalURL,
    index,
    modifier: 'desktopRow',
  });

  const handleRouterLinkClick = useCallback(() => {
    trackEvent({ action: 'click' });
  }, [trackEvent]);

  const handleClick = useCallback(() => {
    onItemSelect(itemId);
    trackEvent({ action: 'click' });
  }, [onItemSelect, itemId, trackEvent]);

  const preventPanelClickBubbling = (event: MouseEvent) =>
    event.stopPropagation();

  const earningsGrowthFormatted = formatterPercentage(earningsGrowth);
  const salesGrowthFormatted = formatterPercentage(salesGrowth);

  const points = useNormalisedCompanySnowflakeScore(company.score);
  const defaultNarrative = listItem.company.valuationOptions.find(
    (valuationOption) =>
      valuationOption.type === 'DCF' && valuationOption.narrative
  );

  return (
    <Track>
      <TableRowWrapper tabIndex={0} role="row" aria-label={companyName}>
        <IntersectionObserver
          threshold={[1]}
          onChange={({ intersectionRatio }) => {
            setIsSticky(intersectionRatio < 1);
          }}
        >
          <TableRowCell isSticky={isSticky}>
            <Box
              marginLeft="x2"
              marginRight="x1"
              height="96px"
              onClick={handleClick}
            >
              <Grid gap="8px">
                <Grid
                  templateColumns="48px auto"
                  alignItems="start"
                  justifyContent="start"
                  gap="8px"
                  role={'rowheader'}
                >
                  <Snowflake width={48} points={points}>
                    <Snowflake.DotMarkers />
                  </Snowflake>
                  <Box>
                    <RouterLink
                      to={canonicalURL}
                      noUnderline
                      onClick={handleRouterLinkClick}
                    >
                      <Text
                        data-cy-id="ticker-symbol"
                        typography="xSmallStrong"
                        color="brand01"
                        textTransform="uppercase"
                      >
                        <strong>{tickerSymbol}</strong>
                      </Text>
                    </RouterLink>
                    <Text
                      typography="xSmall"
                      color="whiteRGBA"
                      opacity={0.5}
                      lineClamp={1}
                    >
                      {companyName}
                    </Text>
                  </Box>
                </Grid>
                <Box marginLeft="x1">
                  <Grid
                    templateColumns="48px 48px"
                    alignItems="center"
                    justifyContent="flex-start"
                    gap="8px"
                  >
                    <Grid alignItems="center" data-cy-id="7-day-return">
                      <Text as="span" typography="xSmall">
                        <PositiveNegativeValue value={return7d}>
                          {formatterPercentage(return7d)}
                        </PositiveNegativeValue>
                      </Text>
                      <Text as="span" typography="xSmall" color="textSofter">
                        7D
                      </Text>
                    </Grid>
                    <Grid alignItems="center" data-cy-id="period-return">
                      <Text as="span" typography="xSmall">
                        <PositiveNegativeValue value={companyReturn}>
                          {formatterPercentage(companyReturn)}
                        </PositiveNegativeValue>
                      </Text>
                      <Text as="span" typography="xSmall" color="textSofter">
                        {period}
                      </Text>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Box>
          </TableRowCell>
        </IntersectionObserver>
        <TableRowCell>
          <Box marginRight="x2" height="96px">
            <Grid gap="8px" templateRows="max-content 40px">
              <Grid
                templateColumns="max-content max-content"
                justifyContent="space-between"
                alignItems="center"
                onClick={handleClick}
              >
                <Text typography="xSmall" isTruncated data-cy-id="share-price">
                  {formatterCurrency(lastSharePrice, companyCurrencyISO)}
                </Text>
                <Text as="span" typography="tinyCondensed">
                  <CompanyIntrinsicDiscount
                    lastSharePrice={lastSharePrice}
                    npvPerShare={npvPerShare}
                    fairValue={
                      narrativeFairValue || defaultNarrative?.fairValue
                    }
                  />
                </Text>
              </Grid>
              <DeferRender>
                <DeprecatedNarrativeMiniPriceChart
                  companyId={companyId}
                  period={period}
                  currencyISO={companyCurrencyISO}
                  fairValue={narrativeFairValue || defaultNarrative?.fairValue}
                />
              </DeferRender>
            </Grid>
          </Box>
        </TableRowCell>
        {primaryNarrative ? (
          <TableRowCell>
            <Box marginRight="x2" marginY="x2">
              {primaryNarrative}
            </Box>
          </TableRowCell>
        ) : null}
        <TableRowCell>
          <Grid alignItems="center" paddingRight="x1" onClick={handleClick}>
            <Text
              as="span"
              typography="xSmall"
              onClick={preventPanelClickBubbling}
              data-cy-id="valuation-cell"
            >
              <Tooltip
                key={`table-tooltip-${companyId}`}
                title={
                  <ValuationMetricTooltip
                    pb={formatterFactor(pb)}
                    pe={formatterFactor(pe)}
                    ps={formatterFactor(priceToSales)}
                  />
                }
                light
                zIndex={99999}
              >
                <TooltipLabel aria-label="Valuation metrics">
                  {preferredRelativeMultiple?.toUpperCase()}
                </TooltipLabel>
              </Tooltip>
              {`  ${formatterFactor(valuation)}`}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell onClick={handleClick} aria-label="Growth metrics">
          <Grid alignItems="center" paddingRight="x1">
            <Text
              as="span"
              typography="xSmall"
              onClick={preventPanelClickBubbling}
              data-cy-id="growth-cell"
            >
              <Tooltip
                key={`table-tooltip-${companyId}`}
                title={
                  <GrowthMetricTooltip
                    analystCount={analystCount}
                    earningsGrowth={earningsGrowthFormatted}
                    salesGrowth={salesGrowthFormatted}
                  />
                }
                light
                zIndex={99999}
              >
                <TooltipLabel aria-label="Growth metrics">
                  {earningsGrowth ? 'E' : 'S'}
                </TooltipLabel>
              </Tooltip>
              {`  ${
                earningsGrowth ? earningsGrowthFormatted : salesGrowthFormatted
              }`}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell aria-label="analyst-target">
          <Grid alignItems="center" onClick={handleClick} paddingRight="x1">
            <Text
              as="span"
              typography="xSmall"
              data-cy-id="analyst-target-cell"
            >
              {formatterCurrency(priceTarget, companyCurrencyISO)}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell>
          <Grid
            alignItems="center"
            paddingLeft="x1"
            paddingRight="x2"
            justifyContent="center"
          >
            {moreOptions}
          </Grid>
        </TableRowCell>
      </TableRowWrapper>
    </Track>
  );
};
